import ShaarpLogoWhite from "@/assets/shaarp-logo-main-light.svg";
import ShaarpLogo from "@/assets/shaarp-logo-main.svg";
import { NavUserDropdownMobile } from "@/components/mobile/user-nav-dropdown-mobile";
import { MobileTeamSwitcher } from "@/components/mobile/mobile-team-switcher";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInput,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { ThemeProviderContext } from "@/contexts/theme-provider";
import useAccess from "@/hooks/useAccess";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  keepFirstConversationsPage,
  useConversationsList,
} from "@/hooks/useConversationsList";
import { useTeams } from "@/contexts/teams-provider";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";
import {
  CONVERSATION_TIME_GROUPS,
  groupConversationsByTime,
} from "@/lib/conversation-time-groups";
import { highlightText } from "@/lib/highlight-text";
import {
  openSupportConversation,
  openSupportEmail,
  SUPPORT_EMAIL,
} from "@/lib/support";
import { ConversationDTO } from "@shaarp/common";
import {
  ChevronRightIcon,
  LifeBuoyIcon,
  ListIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  PlusIcon,
  RotateCwIcon,
} from "lucide-react";
import {
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavLink, useLocation, useMatch, useNavigate } from "react-router-dom";

/**
 * The mobile app's navigation, as in the Claude app: the sections at the top —
 * the team, the lists and help, set apart by a rule — the conversations under
 * them, grouped by period as on the desktop, and the account and a new
 * conversation at the bottom. It replaces the tab bar; the chat is the way in, so the sections are
 * only what is not a conversation.
 */
export function MobileAppSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLightTheme } = useContext(ThemeProviderContext);
  const { openMobile, setOpenMobile } = useSidebar();
  const { hasAccess } = useAccess();
  // Pro is the only licence sold. "basic" is only what the API derives for an
  // advisor with no Pro seat (`advisor.service.ts`): the chat stays open to
  // them, as the `/chat` route does; the lists are Pro's.
  const canChat = hasAccess({ requiredLicences: ["basic", "professional"] });
  const activeConversation = useMatch("/chat/:conversationId");
  const isOnLists = useMatch({ path: "/listing", end: false }) !== null;

  // The back gesture closes the drawer, not the screen under it.
  useCloseOnHistoryBack(openMobile, () => setOpenMobile(false));

  // Closed, the drawer keeps only the first page of conversations: the next
  // opening reads that one again, not every page scrolled through before.
  const queryClient = useQueryClient();
  const { teamSelected } = useTeams();
  const teamId = teamSelected?.id;
  useEffect(() => {
    if (!openMobile) keepFirstConversationsPage(queryClient, teamId);
  }, [openMobile, queryClient, teamId]);

  const close = () => setOpenMobile(false);

  // Whatever changed the screen — an item here, the account menu, a link —
  // the drawer does not stay open over the next one.
  const { pathname } = useLocation();
  const shownPathnameRef = useRef(pathname);
  useEffect(() => {
    if (shownPathnameRef.current === pathname) return;
    shownPathnameRef.current = pathname;
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  // An item for the screen already on show only closes the drawer: a link to
  // the current URL would replace the drawer's history entry and cost a back.
  const closeIfCurrent = (isCurrent: boolean) => (event: MouseEvent) => {
    if (isCurrent) event.preventDefault();
    close();
  };

  return (
    <Sidebar>
      <SheetTitle className="sr-only">
        {t("components.mobileShell.navigation")}
      </SheetTitle>
      <SheetDescription className="sr-only">
        {t("components.mobileShell.navigation")}
      </SheetDescription>
      <SidebarHeader className="px-4 pt-[calc(env(safe-area-inset-top,0px)+1rem)] pb-2">
        <img
          src={isLightTheme ? ShaarpLogo : ShaarpLogoWhite}
          alt="Shaarp"
          className="self-start h-9"
        />
      </SidebarHeader>
      <SidebarContent className="overscroll-y-contain">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* The team first: everything under it belongs to that team.
                  Switching reloads the app, so nothing to close first. */}
              <SidebarMenuItem>
                <MobileTeamSwitcher />
              </SidebarMenuItem>
              {hasAccess({ requiredLicences: ["professional"] }) && (
                <SidebarMenuItem>
                  <SidebarMenuButton size="touch" isActive={isOnLists} asChild>
                    <NavLink
                      to="/listing"
                      onClick={closeIfCurrent(pathname === "/listing")}
                    >
                      <ListIcon />
                      <span>{t("components.mobileShell.lists")}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SupportMenuItem onLeave={close} />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {/* The sections end here; the conversations start under it. */}
        <SidebarSeparator />
        {/* A seat without the chat (`/chat` asks for one) has no
            conversations to list, and none to start. */}
        {canChat && (
          <ConversationsSection
            activeConversationId={activeConversation?.params.conversationId}
            onOpen={closeIfCurrent}
          />
        )}
      </SidebarContent>
      <SidebarFooter className="flex-row gap-3 items-center px-4 pt-2 pb-[calc(var(--mobile-bottom-inset)+0.75rem)]">
        <NavUserDropdownMobile onLeave={close} hasTeamAndHelp={false} />
        {canChat && (
          <Button
            className="flex-1 h-11 text-base rounded-full"
            onClick={() => {
              close();
              // Already on the welcome screen: nothing to push.
              if (pathname !== "/chat") navigate("/chat");
            }}
          >
            <PlusIcon className="size-5" />
            {t("components.mobileShell.newConversation")}
          </Button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

/** An empty state inside the sidebar's narrow column, not a screen's. */
const COMPACT_EMPTY = "gap-3 p-3 md:p-3";

/** Pause after the last key before the search goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Help, under the sections as the account menu had it: the live chat and the
 * support mailbox, folded until asked for.
 */
function SupportMenuItem({ onLeave }: { readonly onLeave: () => void }) {
  const { t } = useTranslation();

  return (
    <Collapsible asChild className="group/support">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton size="touch">
            <LifeBuoyIcon />
            <span>{t("components.navUserDropdownMobile.help.title")}</span>
            <ChevronRightIcon className="ml-auto transition-transform group-data-[state=open]/support:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                className="h-11 text-base [&>svg]:size-5"
              >
                <button
                  type="button"
                  onClick={() => {
                    onLeave();
                    openSupportConversation();
                  }}
                >
                  <MessageCircleIcon />
                  <span>
                    {t("components.navUserDropdownMobile.help.liveChat")}
                  </span>
                </button>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                asChild
                className="h-auto min-h-11 py-1.5 text-base [&>svg]:size-5"
              >
                <button
                  type="button"
                  onClick={() => {
                    onLeave();
                    openSupportEmail();
                  }}
                >
                  <MailIcon />
                  <span className="flex flex-col">
                    {t("components.navUserDropdownMobile.help.sendEmail")}
                    <span className="text-xs text-muted-foreground">
                      {SUPPORT_EMAIL}
                    </span>
                  </span>
                </button>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

/**
 * The search field over the conversations, as on the desktop: server-side,
 * over the conversations' titles and messages, once the user pauses.
 */
function ConversationsSection({
  activeConversationId,
  onOpen,
}: {
  readonly activeConversationId: string | undefined;
  readonly onOpen: (isCurrent: boolean) => (event: MouseEvent) => void;
}) {
  const [search, setSearch] = useState("");

  return (
    <>
      <ConversationsSearchField onSearch={setSearch} />
      <RecentConversations
        search={search}
        activeConversationId={activeConversationId}
        onOpen={onOpen}
      />
    </>
  );
}

/**
 * The field holds what is typed; the list hears of it once the user pauses.
 * A component of its own, so a key re-renders the field, not every
 * conversation under it.
 */
function ConversationsSearchField({
  onSearch,
}: {
  readonly onSearch: (search: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const debounced = useDebouncedValue(value.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    onSearch(debounced);
  }, [debounced, onSearch]);

  return (
    <SidebarGroup className="pb-0">
      <SidebarGroupContent>
        <SidebarInput
          type="search"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder={t("components.mobileShell.searchPlaceholder")}
          aria-label={t("components.mobileShell.searchPlaceholder")}
          className="h-11 text-base"
        />
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function RecentConversations({
  search,
  activeConversationId,
  onOpen,
}: {
  readonly search: string;
  readonly activeConversationId: string | undefined;
  readonly onOpen: (isCurrent: boolean) => (event: MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const isSearching = search !== "";
  const {
    data,
    isPending,
    isError,
    refetch,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    isPlaceholderData,
  } = useConversationsList(search);
  // A next page that failed is asked for again by hand: the sentinel would
  // retry it in a loop for as long as it stays in view.
  const loadsNextPageOnSight = hasNextPage && !isFetchNextPageError;
  const sentinelRef = useRef<HTMLLIElement>(null);

  // The next page loads as the end of the list comes into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (
      !sentinel ||
      !loadsNextPageOnSight ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadsNextPageOnSight, isFetchingNextPage, fetchNextPage]);

  const listGroup = (label: string | null, content: ReactNode) => (
    <SidebarGroup key={label ?? "end"} className={cn(!label && "pt-0")}>
      {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
      <SidebarGroupContent>{content}</SidebarGroupContent>
    </SidebarGroup>
  );
  // While nothing is listed yet, the place the conversations will take.
  const recentGroup = (content: ReactNode) =>
    listGroup(
      t(
        isSearching
          ? "components.mobileShell.results"
          : "components.mobileShell.recent",
      ),
      content,
    );

  if (isPending) {
    return recentGroup(
      <SidebarMenu>
        {Array.from({ length: 6 }, (_, index) => (
          <SidebarMenuItem key={index}>
            <SidebarMenuSkeleton />
          </SidebarMenuItem>
        ))}
      </SidebarMenu>,
    );
  }

  // Only a list that never loaded: a next page or a background refresh that
  // fails keeps the conversations already on screen.
  if (isError && !data) {
    return recentGroup(
      <Empty className={COMPACT_EMPTY}>
        <EmptyDescription>
          {t("components.mobileShell.loadError")}
        </EmptyDescription>
        <EmptyContent>
          <Button
            variant="outline"
            className="h-11"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {t("components.mobileShell.retry")}
          </Button>
        </EmptyContent>
      </Empty>,
    );
  }

  // As on the desktop: the pinned conversations, resolved in full by the API
  // on the first page and sorted there (last pinned first), in a section of
  // their own, so an old pinned one never waits at the bottom of the feed;
  // the feed lists the others. A search returns no pinned section: its
  // results, pinned ones included, are one list.
  const pinned = isSearching ? [] : (data.pages[0]?.pinned ?? []);
  const recent = data.pages
    .flatMap((page) => page.data)
    .filter((conversation) => isSearching || !conversation.pinnedAt);

  const renderItem = (conversation: ConversationDTO) => {
    const title =
      conversation.title || t("components.mobileShell.untitledConversation");
    // A result found in a message, not the title: the passage that matched,
    // and a link that scrolls to it, as on the desktop.
    const matched = isSearching ? conversation.matchedMessage : undefined;
    return (
      <SidebarMenuItem key={conversation.id}>
        <SidebarMenuButton
          size="touch"
          isActive={conversation.id === activeConversationId}
          className={cn(matched && "h-auto min-h-11 py-2 items-start")}
          asChild
        >
          <NavLink
            to={
              matched
                ? {
                    pathname: `/chat/${conversation.id}`,
                    search: `?m=${encodeURIComponent(matched.messageId)}`,
                  }
                : `/chat/${conversation.id}`
            }
            onClick={onOpen(conversation.id === activeConversationId)}
          >
            <MessageSquareIcon className={cn(matched && "mt-0.5")} />
            {matched ? (
              <span className="flex flex-col gap-0.5 min-w-0 whitespace-normal">
                <span className="truncate">{highlightText(title, search)}</span>
                <span className="text-xs text-muted-foreground line-clamp-2">
                  {highlightText(`…${matched.snippet.trim()}…`, search)}
                </span>
              </span>
            ) : (
              <span>{isSearching ? highlightText(title, search) : title}</span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  // The feed by period, as on the desktop; a search's results stay one list,
  // in the order the server ranked them.
  const grouped = groupConversationsByTime(recent);
  const recentGroups = isSearching
    ? [{ label: t("components.mobileShell.results"), items: recent }]
    : CONVERSATION_TIME_GROUPS.map((period) => ({
        label: t(`chat.page.timeGroups.${period}`),
        items: grouped[period],
      })).filter((group) => group.items.length > 0);

  return (
    // The previous list, kept on screen while a new search is read, is shown
    // as waiting rather than as the answer.
    <div
      aria-busy={isPlaceholderData || undefined}
      className={cn(
        "transition-opacity",
        isPlaceholderData && "opacity-60",
      )}
    >
      {pinned.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel>
            {t("components.mobileShell.pinned")}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{pinned.map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
      {recent.length === 0 && pinned.length === 0
        ? recentGroup(
            <Empty className={COMPACT_EMPTY}>
              <EmptyDescription>
                {t(
                  isSearching
                    ? "components.mobileShell.noResults"
                    : "components.mobileShell.noConversations",
                )}
              </EmptyDescription>
            </Empty>,
          )
        : (recent.length > 0 || hasNextPage) && (
            <>
              {recentGroups.map((group) =>
                listGroup(
                  group.label,
                  <SidebarMenu>{group.items.map(renderItem)}</SidebarMenu>,
                ),
              )}
              {hasNextPage &&
                listGroup(
                  null,
                  <SidebarMenu>
                    {loadsNextPageOnSight && (
                      <SidebarMenuItem ref={sentinelRef}>
                        <SidebarMenuSkeleton />
                      </SidebarMenuItem>
                    )}
                    {hasNextPage && isFetchNextPageError && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          size="touch"
                          disabled={isFetchingNextPage}
                          onClick={() => void fetchNextPage()}
                        >
                          <RotateCwIcon />
                          <span>{t("components.mobileShell.retry")}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </SidebarMenu>,
                )}
            </>
          )}
    </div>
  );
}
